import React, { useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { courseConversionService, type ConversionLessonRow, type ConversionPreview } from '../../services/courseConversionService';

/**
 * Package A5 (#518): "Als v2-Kopie anlegen" — shows the per-lesson report
 * first (what converts cleanly, what needs a look), then creates the inactive
 * copy and jumps into the builder.
 */
const statusColor: Record<ConversionLessonRow['status'], 'success' | 'warning' | 'error'> = { converted: 'success', partial: 'warning', manual: 'error' };

const ConvertToBlocksDialog: React.FC<{ open: boolean; courseId: number; courseTitle?: string; onClose: () => void }> = ({ open, courseId, courseTitle, onClose }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const navigate = useNavigate();
    const [preview, setPreview] = useState<ConversionPreview | null>(null);
    const [loading, setLoading] = useState(false);
    const [converting, setConverting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setPreview(null);
        courseConversionService.preview(courseId)
            .then((p) => { if (!cancelled) setPreview(p); })
            .catch((e: { response?: { data?: { error?: string } } }) => { if (!cancelled) setError(e?.response?.data?.error ?? translate('lessons.convert.previewError')); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, courseId, translate]);

    const convert = async () => {
        setConverting(true);
        try {
            const result = await courseConversionService.convert(courseId);
            notify(translate('lessons.convert.done', { title: result.course.title }), { type: 'success' });
            onClose();
            navigate(`/courses/${result.course.id}/builder`);
        } catch (e) {
            const err = e as { response?: { data?: { error?: string } } };
            notify(err?.response?.data?.error ?? translate('lessons.convert.error'), { type: 'error' });
        } finally {
            setConverting(false);
        }
    };

    return (
        <Dialog open={open} onClose={converting ? undefined : onClose} maxWidth="md" fullWidth data-testid="convert-dialog">
            <DialogTitle>{translate('lessons.convert.title', { title: courseTitle ?? '' })}</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{translate('lessons.convert.intro')}</Typography>
                {loading && <CircularProgress size={24} />}
                {error && <Typography color="error" data-testid="convert-error">{error}</Typography>}
                {preview && (
                    <>
                        <Typography variant="subtitle2" sx={{ mb: 1 }} data-testid="convert-summary">
                            {translate('lessons.convert.summary', { total: preview.summary.total, converted: preview.summary.converted, partial: preview.summary.partial, manual: preview.summary.manual })}
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{translate('lessons.convert.lesson')}</TableCell>
                                    <TableCell>{translate('lessons.convert.sourceType')}</TableCell>
                                    <TableCell>{translate('lessons.convert.blocks')}</TableCell>
                                    <TableCell>{translate('lessons.convert.status')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {preview.lessons.map((row) => (
                                    <TableRow key={row.sourceLessonId}>
                                        <TableCell>
                                            {row.title}
                                            {row.notes.map((note, i) => <Typography key={i} variant="caption" display="block" color="text.secondary">{note}</Typography>)}
                                        </TableCell>
                                        <TableCell>{row.type}</TableCell>
                                        <TableCell>{row.blockTypes.join(', ') || '–'}</TableCell>
                                        <TableCell><Chip size="small" color={statusColor[row.status]} label={translate(`lessons.convert.statusLabel.${row.status}`)} /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={converting}>{translate('ra.action.cancel')}</Button>
                <Button variant="contained" onClick={() => void convert()} disabled={!preview || converting || preview.summary.total === 0} startIcon={converting ? <CircularProgress size={16} color="inherit" /> : undefined}>
                    {translate('lessons.convert.create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConvertToBlocksDialog;
